import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const ListenerSummary = React.memo(({ currentListeners, isRealIPData, wsConnected, totalListeners, nginxAnalytics, ENFORCED_COUNT }) => {
  const [summaryData, setSummaryData] = useState({
    live: 0,
    today: 0,
    yesterday: 0,
    last7Days: 0,
    last30Days: 0,
    allTime: 0
  });

  // Removed loading state - data updates smoothly without overlays

  // ENFORCE SINGLE SOURCE - NO MORE PROP ISSUES
  const liveCount = ENFORCED_COUNT || 0;

  const fetchSummaryData = useCallback(async () => {
    // Radiolize approach: Only fetch historical data, live count comes from parent
    
    try {
      console.log('📊 Fetching historical listener statistics...');
      const historicalResponse = await axios.get('/api/historical-stats');
      const historicalData = historicalResponse.data;
      
      console.log('📊 Historical stats data:', historicalData);
      
      // Update summary with historical data, synchronized live count from parent
      setSummaryData(prev => ({
        ...prev,
        live: liveCount, // Synchronized with header/all components via nginx analytics
        today: historicalData.today ?? prev.today ?? 0,
        yesterday: historicalData.yesterday ?? prev.yesterday ?? 0,
        last7Days: historicalData.last7Days ?? prev.last7Days ?? 0,
        last30Days: historicalData.last30Days ?? prev.last30Days ?? 0,
        allTime: historicalData.allTime ?? prev.allTime ?? 0
      }));

    } catch (error) {
      console.error('📊 Error fetching historical data:', error);
      // Preserve existing values, always keep synchronized live count
      setSummaryData(prev => ({
        ...prev,
        live: liveCount // Always synchronized with single source of truth
      }));
    }
  }, [liveCount]);

  useEffect(() => {
    fetchSummaryData();
    
    // Auto-update every 10 seconds to sync with other data sources  
    const interval = setInterval(fetchSummaryData, 10000);
    
    return () => clearInterval(interval);
  }, [fetchSummaryData]);

  // Update live count when it actually changes
  useEffect(() => {
    setSummaryData(prev => {
      if (prev.live !== liveCount) {
        return { ...prev, live: liveCount }; // Use liveCount for consistency
      }
      return prev; // No update if value hasn't changed
    });
  }, [liveCount]);

  // Removed loading condition - always show live data

  return (
    <div className="listener-summary">
      <h3>
        Listener Summary
        {wsConnected && (
          <span style={{
            fontSize: '0.7rem',
            color: '#ef4444',
            marginLeft: '0.5rem',
            background: 'var(--bg-tertiary)',
            padding: '0.2rem 0.5rem',
            borderRadius: '10px',
            animation: 'pulse 2s infinite'
          }}>
            🔴 LIVE
          </span>
        )}
        {!wsConnected && isRealIPData && (
          <span style={{
            fontSize: '0.7rem',
            color: 'var(--accent-primary)',
            marginLeft: '0.5rem',
            background: 'var(--bg-tertiary)',
            padding: '0.2rem 0.5rem',
            borderRadius: '10px'
          }}>
            Real IPs
          </span>
        )}
      </h3>
      <div className="summary-stats">
        <div className="summary-stat">
          <div className="stat-label">Live</div>
          <div className="stat-value live-stat-value">{summaryData.live}</div>
        </div>
        <div className="summary-stat">
          <div className="stat-label">Today</div>
          <div className="stat-value">{summaryData.today}</div>
        </div>
        <div className="summary-stat">
          <div className="stat-label">Yest.</div>
          <div className="stat-value">{summaryData.yesterday}</div>
        </div>
        <div className="summary-stat">
          <div className="stat-label">7 Days</div>
          <div className="stat-value">{summaryData.last7Days}</div>
        </div>
        <div className="summary-stat">
          <div className="stat-label">30 Days</div>
          <div className="stat-value">{summaryData.last30Days}</div>
        </div>
        <div className="summary-stat">
          <div className="stat-label">Total</div>
          <div className="stat-value">{summaryData.allTime}</div>
        </div>
      </div>
    </div>
  );
});

ListenerSummary.displayName = 'ListenerSummary';

export default ListenerSummary;